import type { Mapped } from "types";
import { OnboardingApplicationStepAndFieldStatus } from "consts/onboarding";
import type { FieldsStatuses, WithFieldsStatuses } from "features/onboarding/types";
import type { WithFieldsStatus } from "./types";
import { useFieldsStatuses } from "../../hooks";
import { useApplicationDetailContext } from "../../ApplicationDetailProvider";

export const useApplicationStepStatusAnalytics = () => {
    const { getState, getStep } = useApplicationDetailContext();

    const { parseFieldsStatuses } = useFieldsStatuses();

    const computeFieldsStatuses = (
        resourceResponseData: WithFieldsStatuses,
        map: Map<
            OnboardingApplicationStepAndFieldStatus,
            WithFieldsStatus
        >
    ) => {
        const fieldsStatuses: FieldsStatuses = parseFieldsStatuses(resourceResponseData) ?? {};

        Object.values(fieldsStatuses)
            .forEach(status => {
                const stats = map.get(status)!;

                map.set(status, {
                    ...stats,
                    statusTotal: stats.statusTotal + 1
                });
            });
    };

    const getStepFieldsStatuses = () => {
        const map = new Map(initialState());
        let total = 0;

        for (const step of Array.from(getState().keys())) {
            const {
                nestedEntityFiled,
                totalStepFields,
                response
            } = getStep<Mapped<WithFieldsStatuses>>(step);

            if (nestedEntityFiled && Array.isArray(response.data[nestedEntityFiled])) {
                const entities = response.data[nestedEntityFiled] as unknown as WithFieldsStatuses[];

                for (const nestedEntity of entities) {
                    total += totalStepFields;
                    computeFieldsStatuses(nestedEntity, map);
                }
            } else {
                total += totalStepFields;
                computeFieldsStatuses(response.data, map);
            }
        }

        map.forEach((_, status) => {
            map.set(status, {
                ...map.get(status)!,
                total
            });
        });

        return Array.from(map.entries());
    };

    return {
        getStepFieldsStatuses
    };
};

function initialState() {
    return new Map<
        OnboardingApplicationStepAndFieldStatus,
        WithFieldsStatus
    >()
        .set(
            OnboardingApplicationStepAndFieldStatus.Approved,
            statsFactory()
        )
        .set(
            OnboardingApplicationStepAndFieldStatus.AwaitingReview,
            statsFactory()
        )
        .set(
            OnboardingApplicationStepAndFieldStatus.Rejected,
            statsFactory()
        )
        .set(
            OnboardingApplicationStepAndFieldStatus.RequestedChanges,
            statsFactory()
        )
}

function statsFactory({
    statusTotal = 0,
    total = 0
}: Partial<WithFieldsStatus> = {}) {
    return {
        statusTotal,
        total
    };
}

export default useApplicationStepStatusAnalytics;
